import type { BlockWithRevisions } from '@/types';

export interface BlockLayoutInfo {
  columnIndex: number;
  totalColumns: number;
}

export type BlockLayoutMap = Map<string, BlockLayoutInfo>;

export interface BlockLayoutResult {
  layoutMap: BlockLayoutMap;
  hasOverlaps: boolean; // True if any blocks overlap
}

/**
 * Calculates column layout for overlapping blocks.
 * Blocks that overlap in time are placed side-by-side in columns.
 */
export function calculateBlockLayout(blocks: BlockWithRevisions[]): BlockLayoutResult {
  const layoutMap = new Map<string, BlockLayoutInfo>();
  let hasOverlaps = false;

  if (blocks.length === 0) return { layoutMap, hasOverlaps };

  // Filter out cancelled blocks and sort by start time, then by duration (longer first)
  const activeBlocks = blocks
    .filter((b) => b.status !== 'cancelled')
    .sort((a, b) => {
      if (a.startTime !== b.startTime) return a.startTime - b.startTime;
      return b.duration - a.duration; // Longer blocks first when same start
    });

  if (activeBlocks.length === 0) {
    // If only cancelled blocks, give them all column 0
    blocks.forEach((b) => layoutMap.set(b.id, { columnIndex: 0, totalColumns: 1 }));
    return { layoutMap, hasOverlaps };
  }

  // Find collision groups - blocks that transitively overlap
  const visited = new Set<string>();
  const groups: BlockWithRevisions[][] = [];

  function blocksOverlap(a: BlockWithRevisions, b: BlockWithRevisions): boolean {
    const aEnd = a.startTime + a.duration;
    const bEnd = b.startTime + b.duration;
    return a.startTime < bEnd && b.startTime < aEnd;
  }

  function findConnectedBlocks(block: BlockWithRevisions, group: BlockWithRevisions[]) {
    if (visited.has(block.id)) return;
    visited.add(block.id);
    group.push(block);

    for (const other of activeBlocks) {
      if (!visited.has(other.id) && blocksOverlap(block, other)) {
        findConnectedBlocks(other, group);
      }
    }
  }

  // Build collision groups
  for (const block of activeBlocks) {
    if (!visited.has(block.id)) {
      const group: BlockWithRevisions[] = [];
      findConnectedBlocks(block, group);
      groups.push(group);
    }
  }

  // For each group, assign columns using a greedy algorithm
  for (const group of groups) {
    // Sort group by start time
    group.sort((a, b) => a.startTime - b.startTime);

    // Track column end times - columnEnds[i] is when column i becomes free
    const columnEnds: number[] = [];

    for (const block of group) {
      // Find the first column where this block fits
      let assignedColumn = -1;
      for (let col = 0; col < columnEnds.length; col++) {
        if (columnEnds[col] <= block.startTime) {
          assignedColumn = col;
          break;
        }
      }

      // If no existing column works, create a new one
      if (assignedColumn === -1) {
        assignedColumn = columnEnds.length;
        columnEnds.push(0);
      }

      // Update the column's end time
      columnEnds[assignedColumn] = block.startTime + block.duration;

      // Store the column assignment (total columns will be set after)
      layoutMap.set(block.id, { columnIndex: assignedColumn, totalColumns: 0 });
    }

    // Now set totalColumns for all blocks in this group
    const totalColumns = columnEnds.length;
    if (totalColumns > 1) {
      hasOverlaps = true;
    }
    for (const block of group) {
      const info = layoutMap.get(block.id)!;
      info.totalColumns = totalColumns;
    }
  }

  // Handle cancelled blocks - they get their own positioning
  blocks
    .filter((b) => b.status === 'cancelled')
    .forEach((b) => {
      if (!layoutMap.has(b.id)) {
        layoutMap.set(b.id, { columnIndex: 0, totalColumns: 1 });
      }
    });

  return { layoutMap, hasOverlaps };
}
